import React, { useCallback, useEffect, useState } from 'react';
import css from './ConfirmationCard.module.scss';
import { Button, Checkbox, Dialog, RadioButtonGroup, RadioButton, LoadingProgress } from '@framework-controls/core';
import { strings } from 'src/localization';
import SVPButton from 'src/components/SVPButtons/SVPButton';
import classNames from 'classnames';
import { IconRemove, IconEmail, IconText } from 'src/content/icons';
import { SpecData, VehicleRec, ascCtaInteractionInput, ascFormEngagementInput } from 'src/models';
import { ascCtaInteraction, ascFormEngagement, getElementColor } from 'src/helpers/EventHelper/EventHelper';
import { buildApptData, sendSdEvent, getProviderId } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';
import { IAppointmentState } from 'src/store/states';

export interface IProps {
    contactMethod: string;
    changeContactMethodHandler: (method: string) => void;
    makePreferredContact: boolean;
    toggleMakePreferredContact: (isPreferred: boolean) => void;
    preferContactSelect: string;
    setPreferContactSelect: (preferContactSelect: string) => void;
    customerOptedIn?: string;
    dialogActive: boolean;
    closeDialog: () => void;
    buttonsHandler: (id: string) => void;
    customerName: string;
    textOptInMsg?: string;
    dealershipName: string;
    dealershipPhone: string;
    dealershipLongCode: string;
    textCheckReturned?: boolean;
    smsLaunched: string;
    theme: string;
    dealershipExt?: string;
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    locale: string,
    countryCode: string,
    highlightSendConfirm?: boolean;
    appointment?: IAppointmentState;
    specData?: SpecData;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    clearTextCheckReturned: () => void;
    onPreferBothContactMethodChange?: (method: string) => void;
}

const ConfirmationCard = (props: IProps) => {
    const [checkingOptIn, setCheckingOptIn] = useState(false);
    const [optInFail, setOptInFail] = useState(false);
    const [selectedContactMethod, setSelectedContactMethod] = useState('');
    const [selectedRadioContactMethod, setSelectedRadioContactMethod] = useState('');

    const isSDAnalyticsEnabled =
        props.specData?.toyotaTrackingFlag === 'Y' ||
        props.specData?.lexusTrackingFlag === 'Y' ||
        props.specData?.mbzTrackingFlag === 'Y' ||
        props.specData?.mazdaCATrackingFlag === 'Y' ||
        props.specData?.vwTrackingFlag === 'Y';

    const contactMethods = [
        { label: strings.ContactEmail, value: 'email', icon: <IconEmail className={css.confirmIcon} /> },
        { label: strings.ContactTextMessage, value: 'text', icon: <IconText className={css.confirmIcon} /> },
        {
            label: strings.ContactBoth,
            value: 'both',
            icons: [<IconEmail className={css.confirmIcon} />, <IconText className={css.confirmIcon} />],
        },
    ];
    // Filter contact methods to buttons depending on SMS Launched
    const filteredContactMethods = props.smsLaunched === 'Y' ?
        contactMethods :
        contactMethods.filter(method => method.value !== 'text' && method.value !== 'both');

    const buttonWrapperClasses = method => classNames(
        css.buttonWrapper,
        {
            [css.buttonWrapperBlend]: props.theme === 'lexus' || props.theme === 'lexusMonogram' ||
                props.theme === 'colorSensitive',
            [css.fordButtonWrapper]: props.theme === 'ford',
            [css.cadillacButtonWrapper]: props.theme === 'cadillac',
            [css.hondaButtonWrapper]: props.theme === 'honda',
            [css.bmwButtonWrapper]: props.theme === 'bmw',
            [css.inButtonWrapper]: props.theme === 'infiniti',
            [css.subaruButtonWrapper]: props.theme === 'subaru',
            [css.nissanButtonWrapper]: props.theme === 'nissan',
            [css.mbButtonWrapper]: props.theme === 'mercedesBenz',
            [css.mazdaButtonWrapper]: props.theme === 'mazda',
            [css.genesisButtonWrapper]: props.theme === 'genesis',
            [css.acuraButtonWrapper]: props.theme === 'acura',
            [css.selected]: props.contactMethod === method.value,
        },
    );
    const contactMethodHandler = (value: string, label: string, e: any) => {
        props.changeContactMethodHandler(value);
        setSelectedContactMethod(value);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: label,
            elementColor: '',
            elementOrder: '',
            elementType: 'form',
            elementSubType: 'contact_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'service',
            commType: 'form',
            commStatus: 'engage',
            elementText: label,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp, props.vehicles, props.selectedVehicle);

        if (props.appointment && isSDAnalyticsEnabled) {
            sendSdEvent('scheduleServiceReviewStart', buildApptData(props.sessionId,
                getProviderId(props.specData, props.toyotaMCLaunched, props.toyotaTDMCLaunched,
                    props.lexusMCLaunched, props.lexusTDMCLaunched), props.appointment));
        }
    };

    const contactMethodButtons = filteredContactMethods.map((method) => (
        <div className={buttonWrapperClasses(method)} key={method.value}>
            <Button
                id={`contactMethod-${method.value}`}
                onClick={(e: any) => contactMethodHandler(method.value, method.label, e)}
                // variant={props.contactMethod === method.value ? 'contained' : 'outlined'}
                variant='atext'
                size='full'
                type={props.contactMethod === method.value ? 'contained' : 'outlined'}
            >
                <div className={css.iconsContainer}>
                    {method.value === 'both' ? (
                    // Render multiple icons side by side for "Both"
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{ display: 'flex' }}>
                                {method.icons?.map((icon, index) => (
                                    <React.Fragment key={index}>
                                        {React.cloneElement(icon, { className: css.confirmIcon })}
                                    </React.Fragment>
                                ))}
                            </div>
                            <span className={css.label}>{method.label}</span>
                        </div>
                    ) : (
                    // Default rendering for single icon methods
                        <>
                            {method.icon && React.cloneElement(method.icon, { className: css.confirmIcon })}
                            <span className={css.label}>{method.label}</span>
                        </>
                    )}
                </div>
            </Button>
        </div>
    ));

    let dealershipLongCode = props.dealershipLongCode;
    // Format the advisor phone number using regular expressions
    const strippedPhone = ('' + dealershipLongCode).replace(/\D/g, '');
    const match = strippedPhone.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        dealershipLongCode = '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }

    const radioEventHandler = useCallback((e: any) => {
        const elementColor = getElementColor(e, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: selectedRadioContactMethod === 'text' ? strings.ContactTextMessage : strings.ContactEmail,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'item_details',
            elementSubType: 'radio_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'service',
            commType: 'form',
            commStatus: 'engage',
            elementText: selectedRadioContactMethod === 'text' ? strings.ContactTextMessage : strings.ContactEmail,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp, props.vehicles, props.selectedVehicle);
    }, [props.vehicles, props.selectedVehicle, selectedRadioContactMethod]);

    const radioButtons = (
        <React.Fragment>
            <div onClick={(e: any) => radioEventHandler(e)}>
                <RadioButton
                    value='email'
                    label={strings.ContactEmail}
                    color='default'
                />
            </div>
            <div onClick={(e: any) => radioEventHandler(e)}>
                <RadioButton
                    value='text'
                    label={strings.ContactTextMessage}
                    color='default'
                />
            </div>
        </React.Fragment>
    );

    const changeHandler = (id: string, value: string) => {
        switch (id) {
            case 'contactMethod':
            default:
                props.changeContactMethodHandler(value);
                break;

            case 'makePreferredContact': {
                const isPreferred = value === 'true';
                const radioValue = value;
                props.toggleMakePreferredContact(isPreferred);
                setSelectedRadioContactMethod(radioValue);
                props.onPreferBothContactMethodChange?.(radioValue);
                props.setPreferContactSelect(radioValue);
                break;
            }
        }
        if (props.appointment && isSDAnalyticsEnabled) {
            sendSdEvent('scheduleServiceReviewStart', buildApptData(props.sessionId,
                getProviderId(props.specData, props.toyotaMCLaunched, props.toyotaTDMCLaunched,
                    props.lexusMCLaunched, props.lexusTDMCLaunched), props.appointment));
        }
    };

    const okayHandler = useCallback((e: any) => {
        setCheckingOptIn(true);
        props.buttonsHandler('okay');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Okay,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.buttonsHandler, props.vehicles, props.selectedVehicle]);

    // const closeHandler = useCallback(() => {
    //     props.closeDialog();
    // }, [props.closeDialog]);

    const clickYesHandler = useCallback((e: any) => {
        props.buttonsHandler('yes');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Yes,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.buttonsHandler, props.vehicles, props.selectedVehicle]);

    const clickNoHandler = useCallback((e: any, text: string) => {
        setOptInFail(false);
        props.buttonsHandler('no');
        let elementColor = '';
        if (text === '') {
            elementColor = getElementColor(e, 'color');
        }
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: text,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.buttonsHandler, props.vehicles, props.selectedVehicle]);

    useEffect(() => {
        if (props.textCheckReturned && checkingOptIn) {
            setCheckingOptIn(false);
            if (props.customerOptedIn === 'I') {
                props.closeDialog();
                props.clearTextCheckReturned();
            } else {
                // else if user hasn't opted in yet warn them.
                setOptInFail(true);
            }
        }
    }, [checkingOptIn, props.textCheckReturned, props.customerOptedIn, props.closeDialog, props.clearTextCheckReturned]);

    const tenDigitPhoneBuilder = function (phone:string) {
        const tenDigit = `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;

        return tenDigit;
    };

    const internationPhoneBuilder = function (phone:string) {
        const phoneLength = phone.length;
        const intPhone = `(+${phone.slice(0, phoneLength-10)}) ${phone.slice(phoneLength-10, phoneLength-7)}`+
        `-${phone.slice(phoneLength-7, phoneLength-4)}-${phone.slice(phoneLength-4)}`;

        return intPhone;
    };

    const shortPhoneBuilder = function (phone:string) {
        const phoneLength = phone.length;
        const shortPhone = `${phone.slice(0, phoneLength-7)} ${phone.slice(phoneLength-7, phoneLength-4)}`+
        `-${phone.slice(phoneLength-4, phoneLength)}`;

        return shortPhone;
    };

    let longCode = props.dealershipLongCode ?? '';
    if (longCode.length === 10) {
        longCode = tenDigitPhoneBuilder(longCode);
    } else if (longCode.length > 10) {
        longCode = internationPhoneBuilder(longCode);
    } else if (longCode.length && longCode.length < 10){
        longCode = shortPhoneBuilder(longCode);
    }

    let storeNumber = props.dealershipPhone ?? '';
    if (storeNumber?.length === 10) {
        storeNumber = tenDigitPhoneBuilder(storeNumber);
    } else if (storeNumber.length > 10) {
        storeNumber = internationPhoneBuilder(storeNumber);
    } else if (storeNumber.length && storeNumber.length < 10){
        storeNumber = shortPhoneBuilder(storeNumber);
    }

    const storeExt = props.dealershipExt;
    if (storeExt) {
        storeNumber = storeNumber + ' ext.' + storeExt;
    }
    const contact = storeNumber === '' ? '' : strings.OptIn4 + storeNumber;

    const privacyLinkClass = classNames(
        {
            [css.privacyLinkBMW]: props.theme === 'bmw',
            [css.privacyLinkAcura]: props.theme === 'acura',
        },
    );

    const privacyPolicyLink = props.countryCode === 'US' ? <a href='https://www.reyrey.com/ext/service-portal-policy-2_0'
        target='_blank' rel='noreferrer' className={privacyLinkClass} >{strings.OptIn6} </a>: '';
    let optInMessage = (
        <p className={css.optInMsg}>{strings.OptIn1 + props.dealershipName + strings.OptIn2 + longCode + strings.OptIn3 +
            props.dealershipName + contact + strings.OptIn5} {privacyPolicyLink}</p>
    );

    const btnAreaDiv = classNames(
        css.btnArea,
        {
            [css.btnAreaAcura]: props.theme === 'acura' || props.theme === 'acura-CA',
        },
    );

    const customerNameStyles = classNames(
        css.customerName,
        {
            [css.customerNameBMW]: props.theme === 'bmw',
        },
    );

    let buttons = (
        <div className={btnAreaDiv}>
            <SVPButton
                id='No'
                handler={(e: any) => clickNoHandler(e, strings.No)}
                message={strings.No}
                noArrow
                variant={'secondaryBtnAlt'}
                theme={props.theme}
            />
            <SVPButton
                id='yes'
                handler={(e: any) => clickYesHandler(e)}
                message={strings.Yes}
                variant={'primaryBtnAlt'}
                noArrow
                theme={props.theme}
            />
        </div>
    );

    const checkBoxHandler = (e: any) => {
        props.toggleMakePreferredContact(props.makePreferredContact);
        const elementColor = getElementColor(e, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.MakePreferred,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'item_details',
            elementSubType: 'checkbox',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        if (props.appointment && isSDAnalyticsEnabled) {
            sendSdEvent('scheduleServiceReviewStart', buildApptData(props.sessionId,
                getProviderId(props.specData, props.toyotaMCLaunched, props.toyotaTDMCLaunched,
                    props.lexusMCLaunched, props.lexusTDMCLaunched), props.appointment));
        }
    };

    if (props.customerOptedIn === 'T') {
        optInMessage = optInFail ? <p>
            {strings.ViewAcctOptBackInFail + longCode + strings.ViewAcctOptBackInFail2}</p> :
            <p>{strings.ViewAcctOptBackIn1 + longCode + strings.ViewAcctOptBackIn2}</p>;
        buttons = (
            <div className={css.btnArea}>
                <SVPButton
                    id='cancel'
                    handler={(e: any) => clickNoHandler(e, strings.CancelButton)}
                    message={strings.CancelButton}
                    noArrow
                    variant={'secondaryBtnAlt'}
                    theme={props.theme}
                />
                <SVPButton
                    id='okay'
                    handler={okayHandler}
                    message={strings.Okay}
                    variant={'primaryBtnAlt'}
                    noArrow
                    theme={props.theme}
                />
            </div>
        );
    }

    let cbkClassName = 'primaryCkb';
    if (props.makePreferredContact) {
        cbkClassName = 'primaryCkbChecked';
    }

    const makePreferredChkBoxStyles = classNames(
        css.makePreferredChkBox,
        {
            [css.makePreferredChkBoxBMW]: props.theme === 'bmw',
        },
    );

    return (
        <div>
            <p
                className={classNames(css.sendConfirmation, {
                    [css.highlightSendConfirm]: props.highlightSendConfirm,
                })}
            >
                {strings.SendConfirmation}
            </p>
            <div id='contactMethodButtons'>
                {contactMethodButtons}

            </div>
            {selectedContactMethod !== 'both' && (
                <div className={makePreferredChkBoxStyles}>
                    <Checkbox
                        id='makePreferredContact'
                        size={'rg'}
                        variant={cbkClassName}
                        checked={props.makePreferredContact}
                        onChange={(e: any) => checkBoxHandler(e)}
                        label={strings.MakePreferred}
                        labelPlacement='end'
                    />
                </div>
            )}
            {selectedContactMethod === 'both' && (
                <div>
                    <p className={css.makePreferred}>{strings.MakePreferred}</p>
                    <div className={css.radioButton}>
                        <RadioButtonGroup
                            id='makePreferredContact'
                            value={selectedRadioContactMethod}
                            onChange={changeHandler}
                            orientation='horizontal'
                        >
                            {radioButtons}
                        </RadioButtonGroup>
                    </div>
                </div>
            )}

            <Dialog
                cssClasses={{
                    content: css.content,
                    // contextHeader: css.header,
                    paper: css.paper,
                    header: css.LightBoxHeader,
                }}
                header={strings.TextOptIn}
                isOpen={props.dialogActive}
                hideSave
                hideCancel
                onBackDropClick={(e: any) => clickNoHandler(e, '')}
                onCancel={(e: any) => clickNoHandler(e, '')}
                noBottomPadding
                width={'full'}
                // noLeftRightPadding={true}
                // onCustomClicked={(_, id) => props.buttonsHandler(id)}
            >
                {checkingOptIn ? <LoadingProgress cssClasses={{ spinner: css.progressColor }} size={100} /> : null}
                <div className={css.headerbox}>
                    {strings.TextOptIn}
                    <div className={css.headerIcon}>
                        <IconRemove id='closeBtn' onClick={(e: any) => clickNoHandler(e, '')}/>
                    </div>
                </div>
                <div>
                    <p className={customerNameStyles}>{props.customerName},</p>
                    {optInMessage}
                    <p className={css.textOptInMsg}>{props.textOptInMsg}</p>
                </div>
                <div className={css.footer}>
                    <hr className={css.horizLine}/>
                    {buttons}
                </div>
            </Dialog>
        </div>
    );
};

export default ConfirmationCard;